(() => {
  const trackingKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];

  function initialize(root) {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    root.querySelectorAll('[data-osp-article-date]').forEach((element) => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() - 2);
      element.dateTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      element.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        year: 'numeric', month: 'long', day: 'numeric',
      }).format(date);
    });

    const incoming = new URLSearchParams(location.search);
    root.querySelectorAll('[data-product-link]').forEach((link) => {
      if (!link.getAttribute('href')) {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        return;
      }
      const target = new URL(link.getAttribute('href'), location.origin);
      for (const key of trackingKeys) if (incoming.has(key)) target.searchParams.set(key, incoming.get(key));
      link.href = target.toString();
    });

    root.querySelectorAll('[data-osp-accordion]').forEach((accordion) => {
      const toggle = accordion.querySelector('[data-accordion-toggle]');
      const panel = accordion.querySelector('[data-accordion-panel]');
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
        accordion.dataset.state = expanded ? 'closed' : 'open';
      });
    });

    root.querySelector('[data-faq-more]')?.addEventListener('click', (event) => {
      root.querySelectorAll('[data-faq-extra]').forEach((item) => { item.hidden = false; });
      event.currentTarget.setAttribute('aria-expanded', 'true');
      event.currentTarget.hidden = true;
    });

    root.querySelectorAll('[data-osp-carousel]').forEach((carousel) => {
      const viewport = carousel.querySelector('[data-carousel-viewport]');
      const track = carousel.querySelector('[data-carousel-track]');
      if (!viewport || !track) return;
      const slides = [...track.querySelectorAll(':scope > [data-carousel-slide]')];
      if (!slides.length) return;
      const thumbnails = [...(carousel.closest('[data-gallery]')?.querySelectorAll('[data-gallery-thumb]') || [])];
      const dots = [...carousel.querySelectorAll('[data-carousel-dot]')];
      const activeDotClass = carousel.dataset.carouselActiveClass;
      const next = carousel.querySelector('[data-carousel-direction="next"]');
      const previous = carousel.querySelector('[data-carousel-direction="previous"]');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      let current = 0;
      let startX = null;
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        const visibleWidth = viewport.clientWidth;
        const offset = Math.max(0, Math.min(slides[current].offsetLeft - slides[0].offsetLeft, track.scrollWidth - visibleWidth));
        track.style.setProperty('transform', `translateX(-${offset}px)`, 'important');
        dots.forEach((dot, i) => {
          const active = i === current;
          dot.setAttribute('aria-current', String(active));
          if (activeDotClass) dot.classList.toggle(activeDotClass, active);
        });
        thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === current)));
        const activeThumb = thumbnails[current];
        const thumbViewport = activeThumb?.closest('[data-carousel-viewport]');
        if (thumbViewport) {
          const left = activeThumb.offsetLeft;
          const right = left + activeThumb.offsetWidth;
          if (left < thumbViewport.scrollLeft || right > thumbViewport.scrollLeft + thumbViewport.clientWidth) {
            thumbViewport.scrollTo({ left: left < thumbViewport.scrollLeft ? left : right - thumbViewport.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
          }
        }
        if (previous) previous.disabled = false;
        if (next) next.disabled = false;
        slides.forEach((slide, i) => {
          const left = slide.offsetLeft - slides[0].offsetLeft;
          const visible = left + slide.offsetWidth > offset && left < offset + visibleWidth;
          slide.setAttribute('aria-hidden', String(!visible));
          slide.inert = !visible;
        });
      };
      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => show(index));
        thumb.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(index); }
        });
      });
      next?.addEventListener('click', () => show(current + 1));
      previous?.addEventListener('click', () => show(current - 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
      viewport.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        show(current + (event.key === 'ArrowRight' ? 1 : -1));
      });
      viewport.tabIndex = 0;
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea')) return;
        startX = event.clientX;
        viewport.setPointerCapture(event.pointerId);
      });
      viewport.addEventListener('pointerup', (event) => {
        if (startX !== null && Math.abs(event.clientX - startX) > 45) show(current + (event.clientX < startX ? 1 : -1));
        startX = null;
      });
      viewport.addEventListener('pointercancel', () => { startX = null; });
      viewport.addEventListener('dragstart', (event) => event.preventDefault());
      if (!reducedMotion) track.style.transition = 'transform 200ms ease';
      new ResizeObserver(() => show(current)).observe(viewport);
      show(0);
    });

    root.querySelectorAll('[data-shipping-countdown]').forEach((banner) => {
      const duration = Number(banner.dataset.duration) * 1000;
      const storageKey = 'ortosonicpod-shipping-deadline';
      let deadline = Date.now() + duration;
      try {
        const stored = Number(sessionStorage.getItem(storageKey));
        if (stored > 0 && stored <= deadline) deadline = stored;
        sessionStorage.setItem(storageKey, String(deadline));
      } catch (_) { /* The timer also works when browser storage is unavailable. */ }
      const timer = banner.querySelector('[data-countdown-time]');
      const update = () => {
        const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        if (!remaining) {
          banner.textContent = banner.dataset.expiredLabel;
          return false;
        }
        timer.textContent = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
        return true;
      };
      const active = update();
      banner.hidden = false;
      if (active) {
        const interval = setInterval(() => {
          if (!banner.isConnected || !update()) clearInterval(interval);
        }, 1000);
      }
    });

    const form = root.querySelector('.osp-offer-form');
    const button = root.querySelector('[data-add-to-cart]');
    if (!form || !button) {
      if (button) button.disabled = true;
      return;
    }
    const variantField = form.querySelector('[data-selected-variant]');
    const radios = [...form.querySelectorAll('[data-pack-variant]')];
    const select = (radio) => {
      variantField.value = radio.value;
      radios.forEach((item) => item.closest('.osp-offer').classList.toggle('is-selected', item.checked));
      button.disabled = radio.disabled;
    };
    radios.forEach((radio) => radio.addEventListener('change', () => select(radio)));
    const selected = radios.find((radio) => radio.checked);
    button.disabled = !selected || selected.disabled;
    root.querySelectorAll('[data-offer-link]').forEach((link) => link.addEventListener('click', () => {
      const offers = root.querySelector('#ortosonicpod-offers') || form;
      offers.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }));
    button.addEventListener('click', () => form.requestSubmit());

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (button.disabled || !variantField.value) return;
      const error = root.querySelector('[data-cart-error]');
      error.hidden = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const variantId = variantField.value;
      let added = false;
      try {
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: ['cart-drawer-section'], sections_url: location.pathname }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || root.dataset.cartError);
        added = true;
        if (!result.sections?.['cart-drawer-section'] || !document.querySelector('theme-drawer#cart-drawer')) {
          location.assign(root.dataset.cartUrl);
          return;
        }
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const cartResponse = await fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(root.dataset.cartError);
        const cart = await cartResponse.json();
        root.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'ortosonicpod', itemCount: cart.item_count, sections: result.sections, didError: false },
          }),
        }));
        await customElements.whenDefined('theme-drawer');
        document.querySelector('theme-drawer#cart-drawer')?.open();
      } catch (failure) {
        if (added) {
          location.assign(root.dataset.cartUrl);
          return;
        }
        error.textContent = failure.message || root.dataset.cartError;
        error.hidden = false;
      } finally {
        button.disabled = !selected || radios.find((radio) => radio.checked)?.disabled;
        button.removeAttribute('aria-busy');
      }
    });
  }

  document.querySelectorAll('[data-ortosonicpod-page]').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target.querySelector('[data-ortosonicpod-page]')));
})();
